import { Interval, WEEK_PARITIES, WeekParity } from '../../../../models/Interval';

const weekParityTitles: Record<WeekParity, string> = {
  even: 'Jämna veckor',
  odd: 'Ojämna veckor',
};

const weekParityLabels: Record<WeekParity, string> = {
  even: 'per jämn vecka',
  odd: 'per ojämn vecka',
};

const isWeekParity = (value: unknown): value is WeekParity =>
  WEEK_PARITIES.includes(value as WeekParity);

type IntervalFrequency = Pick<Interval, 'frequency' | 'intervalType' | 'weekParity'>;

type IntervalFrequencyOption = {
  title: string;
  value: string;
};

export const getFrequencyTitle = (count: number, intervalType: string): string => {
  let interval = '';

  switch (intervalType) {
    case 'weekly':
      interval = 'vecka';
      break;
    case 'monthly':
      interval = 'månad';
      break;
    case 'daily':
      interval = 'dag';
      break;
    default:
      return '';
  }

  if (count === 0) {
    return 'En gång';
  }

  if (count === 1) {
    return `Varje ${interval}`;
  }

  if (count === 2) {
    return `Varannan ${interval}`;
  }

  return `Var ${count}:e ${interval}`;
};

/**
 * Generates the dropdown options for the frequency selector.
 *
 * The maximum selectable frequency differs per interval type:
 * - weekly  → up to every 13 weeks  (~quarterly)
 * - daily   → up to every 14 days   (bi-weekly)
 * - monthly → up to every 12 months (annually)
 *
 * @param intervalType – The current interval type
 * @returns Array of {title, value} objects for a Dropdown component
 */
export const intervalWeeklyFrequencyOptions = (intervalType: string): IntervalFrequencyOption[] => {
  let arrayLength = 0;

  switch (intervalType) {
    case 'weekly':
      arrayLength = 13;
      break;
    case 'daily':
      arrayLength = 14;
      break;
    case 'monthly':
      arrayLength = 12;
      break;
  }

  const frequencyArray = Array.from({ length: arrayLength }, (_, index) => {
    const count = index + 1;

    return {
      title: getFrequencyTitle(count, intervalType),
      value: `${count}`,
    };
  });

  if (intervalType === 'weekly') {
    return [
      ...frequencyArray,
      { title: weekParityTitles.even, value: 'even' },
      { title: weekParityTitles.odd, value: 'odd' },
    ];
  }

  return frequencyArray;
};

export const getFrequencyLabel = (frequency: number, intervalType: string): string => {
  const label = getFrequencyTitle(frequency, intervalType);

  if (!label) {
    return label;
  }

  return label.charAt(0).toLowerCase() + label.slice(1);
};

/**
 * The title of an interval's frequency, e.g. "Varannan vecka" or "Jämna veckor".
 *
 * Use this rather than `getFrequencyTitle(Number(interval.frequency), …)`: a week-parity
 * interval has an empty frequency, which `getFrequencyTitle` would show as "En gång".
 */
export const getIntervalFrequencyTitle = (interval: IntervalFrequency): string =>
  interval.weekParity
    ? weekParityTitles[interval.weekParity]
    : getFrequencyTitle(Number(interval.frequency), interval.intervalType);

/** The lower-case frequency for running text, e.g. "Antal tillfällen per jämn vecka". */
export const getIntervalFrequencyLabel = (interval: IntervalFrequency): string =>
  interval.weekParity
    ? weekParityLabels[interval.weekParity]
    : getFrequencyLabel(Number(interval.frequency), interval.intervalType);

/** The value the Frekvens dropdown shows as selected: the parity, or the numeric frequency. */
export const getFrequencyOptionValue = (interval: IntervalFrequency): string =>
  interval.weekParity ?? interval.frequency ?? '';

/** Whether the interval has a frequency chosen, either a number of weeks or a parity. */
export const hasFrequency = (interval: IntervalFrequency): boolean =>
  Boolean(interval.weekParity || interval.frequency);

/**
 * How many weeks, months or days pass between occurrences, for annualising prices.
 *
 * A week-parity interval runs every other week on average, so it prices like "Varannan
 * vecka". Returns 0 when no frequency is chosen yet.
 */
export const getPeriodsBetweenOccurrences = (
  interval: Pick<Interval, 'frequency' | 'weekParity'>
): number => (interval.weekParity ? 2 : Number(interval.frequency) || 0);

/**
 * Applies a change of one interval field, keeping the week parity consistent with it.
 *
 * Choosing "Jämna veckor" or "Ojämna veckor" (the values 'even'/'odd' of the Frekvens
 * dropdown) sets the parity and empties the frequency, since the backend accepts only one of
 * them. Choosing a number of weeks clears the parity, and so does any change that leaves the
 * interval not weekly, whichever field caused it.
 *
 * Every interval form changes its state through this, so no path can leave a stale parity.
 *
 * @param interval - The interval before the change
 * @param name - The changed field
 * @param value - Its new value
 * @returns The interval after the change
 */
export const applyIntervalChange = <
  T extends Pick<Interval, 'frequency' | 'intervalType' | 'weekParity'>,
>(
  interval: T,
  name: string,
  value: unknown
): T => {
  const changed: T =
    name === 'frequency'
      ? {
          ...interval,
          frequency: isWeekParity(value) ? '' : (value as string),
          weekParity: isWeekParity(value) ? value : null,
        }
      : { ...interval, [name]: value };

  return changed.intervalType === 'weekly' || !changed.weekParity
    ? changed
    : { ...changed, weekParity: null };
};
